import classnames from "classnames";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import { getHtmlProps } from "web/script/modules/html-tag-props";
import { languageIsEnglish } from "web/script/modules/language";
import userProfiler from "web/script/modules/userprofiler";
import PropTypes from "prop-types";
import React from "react";
import { connect } from "react-redux";
import _ from "underscore";
import { openEmailCaptureOverlay } from "web/redux/ducks/email-capture-overlay";
import withReduxProvider from "../../../redux-provider";
import PrimaryButton from "../../buttons/primary-button/primary-button";
import HintBubble from "../../hint-bubble/hint-bubble";
import LeadLink from "../../lead-link/lead-link";
import SaveForLaterButton from "../../save-for-later-button/save-for-later-button";
import SVGIcon from "../../svg-icon/svg-icon";
import styles from "./product-description-price.css";

const PromoPropType = PropTypes.shape({
    message: PropTypes.string.isRequired,
    url: PropTypes.string,
    tcs: PropTypes.arrayOf(
        PropTypes.shape({
            message: PropTypes.string.isRequired,
            url: PropTypes.string.isRequired
        })
    ).isRequired
});

function DisplayPromos({ promotion }) {
    if (!promotion) {
        return null;
    }
    let { message, url, tcs } = promotion;

    message = <span className={styles.promoText} dangerouslySetInnerHTML={{ __html: message }} />;

    let promoText;
    if (url) {
        promoText = <a href={url}>{message}</a>;
    } else {
        promoText = message;
    }

    tcs = tcs.map(({ message, url }, i) => (
        <a href={url} key={i}>
            {message}
        </a>
    ));
    let tcsPopup = <div className={styles.promoTCs}>{tcs}</div>;

    return (
        <div className={styles.promoArea}>
            <SVGIcon name="tag" className={styles.promoIcon} />
            {promoText}
            <HintBubble tooltipText={tcsPopup} className={styles.promoTCsIcon} />
        </div>
    );
}

DisplayPromos.propTypes = {
    promotion: PromoPropType
};

export function ProductDescriptionPrice({
    absoluteUrl,
    alwaysShowLargeCTA,
    className,
    currentPriceHtml,
    dateUpdated,
    isAmazonProduct,
    isLoggedIn,
    isOnSale,
    isSavedForLater,
    lastChecked,
    leadLabel,
    leadUrl,
    openEmailCaptureOverlay,
    priceWithCurrencySymbol,
    productId,
    promotion,
    retailerName,
    shippingCostLabel
}) {
    const fromSpanProps = {
        class: styles.from
    };
    // we escape retailerName, and so mark this string as available for dangerouslySetInnerHTML
    const fromRetailerLabelSafeHtml = {
        __html: gettext("in_stock_product.from_retailer.in_spans", {
            from_span_props: getHtmlProps(fromSpanProps),
            retailer_name_span_props: "",
            retailer_name: _.escape(retailerName)
        })
    };

    function onClickBuyNow() {
        if (!userProfiler.isLoggedIn() && environment.get("boomLeadSignupPrompt")) {
            openEmailCaptureOverlay({
                productId: productId,
                captureType: "signup_lead_save"
            });
        }
    }

    return (
        <div
            className={classnames(className, styles.wrapper)}
            itemProp="offers"
            itemScope
            itemType="http://schema.org/Offer"
        >
            <meta itemProp="url" content={absoluteUrl} />
            <meta itemProp="availability" content="http://schema.org/InStock" />
            <meta itemProp="itemCondition" content="http://schema.org/NewCondition" />
            <div className={classnames(styles.priceWrapper)}>
                <div className={styles.priceArea}>
                    <span
                        className={classnames(styles.price, {
                            [styles.salePrice]: isOnSale
                        })}
                        dangerouslySetInnerHTML={{ __html: currentPriceHtml }}
                    />
                    {isOnSale && (
                        <span className={styles.originalPrice}>
                            {" "}
                            <del>{priceWithCurrencySymbol}</del>
                        </span>
                    )}
                    <div className={styles.retailArea}>
                        <LeadLink
                            reason="retailer-link"
                            href={leadUrl}
                            isSavedForLater={isSavedForLater}
                        >
                            <span
                                className={styles.retailer}
                                dangerouslySetInnerHTML={fromRetailerLabelSafeHtml}
                            />
                        </LeadLink>
                    </div>
                    {shippingCostLabel && (
                        <span className={styles.shippingCost}>{shippingCostLabel}</span>
                    )}
                </div>
                {!alwaysShowLargeCTA && <div className={styles.leadArea}>
                    {/* Affiliate PDP mobile */}
                    <LeadLink
                        reason="buy-on-store-button"
                        href={leadUrl}
                        openInNewTab={true}
                        afterOnClick={onClickBuyNow}
                        productId={productId}
                        isSavedForLater={isSavedForLater}
                    >
                        <PrimaryButton fullHeight fullWidth element="span">
                            {leadLabel}
                        </PrimaryButton>
                    </LeadLink>
                    {languageIsEnglish() && (
                        <div>
                            <SaveForLaterButton
                                initialIsSaved={isSavedForLater}
                                isLoggedIn={isLoggedIn}
                                type="normal"
                                fullWidth={false}
                                productId={productId}
                                pageSource="pdp"
                                cta={gettext("designer_updates.track_deals_btn_text")}
                            />
                        </div>
                    )}
                </div>}
            </div>
            <div className={classnames(
                styles.largeCTAAreaWrapper,
                {[styles.largeCTAAreaWrapperPersistent]: alwaysShowLargeCTA})
            }>
                {/* Affiliate PDP desktop & Express Checkout PDP */}
                <div className={styles.visitSiteCTA}>
                    <LeadLink
                        reason="buy-on-store-button"
                        href={leadUrl}
                        openInNewTab={true}
                        afterOnClick={onClickBuyNow}
                        productId={productId}
                        isSavedForLater={isSavedForLater}
                    >
                        <PrimaryButton element="span" onClick={onClickBuyNow} fullWidth>
                            {leadLabel}
                        </PrimaryButton>
                    </LeadLink>
                </div>
                <div className={styles.saveForLaterCTA}>
                    <SaveForLaterButton
                        initialIsSaved={isSavedForLater}
                        isLoggedIn={isLoggedIn}
                        type="normal"
                        productId={productId}
                        pageSource="pdp"
                    />
                </div>
            </div>
            <DisplayPromos promotion={promotion} />
            {lastChecked && isAmazonProduct && (
                <div className={styles.lastCheckedWrapper}>
                    <HintBubble tooltipText={gettext("in_stock_product.last_checked_at_tooltip")} />
                    <span title={dateUpdated}>{lastChecked}</span>
                </div>
            )}
        </div>
    );
}

function mapDispatchToProps(dispatch) {
    return {
        openEmailCaptureOverlay: payload => dispatch(openEmailCaptureOverlay(payload))
    };
}

ProductDescriptionPrice.displayName = "ProductDescriptionPrice";

ProductDescriptionPrice.defaultProps = {
    alwaysShowLargeCTA: false,
    isAmazonProduct: false,
    isSavedForLater: false,
    isOnSale: false
};

ProductDescriptionPrice.propTypes = {
    absoluteUrl: PropTypes.string.isRequired,
    alwaysShowLargeCTA: PropTypes.bool,
    className: PropTypes.string,
    currentPriceHtml: PropTypes.string.isRequired,
    dateUpdated: PropTypes.string,
    isAmazonProduct: PropTypes.bool,
    isLoggedIn: PropTypes.bool,
    isOnSale: PropTypes.bool,
    isSavedForLater: PropTypes.bool,
    lastChecked: PropTypes.string,
    leadLabel: PropTypes.string.isRequired,
    leadUrl: PropTypes.string.isRequired,
    openEmailCaptureOverlay: PropTypes.func,
    priceWithCurrencySymbol: PropTypes.string.isRequired,
    productId: PropTypes.string.isRequired,
    promotion: PromoPropType,
    retailerName: PropTypes.string,
    shippingCostLabel: PropTypes.string
};

export default withReduxProvider(
    connect(
        null,
        mapDispatchToProps
    )(ProductDescriptionPrice)
);
